import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { handleErrors, validateImage } from "../../services/dataManager";
import {
  addNewBadge,
  updateBadge,
  uploadBadgeIcon,
} from "../../services/axios/apiManager";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../Components/spinner";
import { baseURL } from "../../services/axios/eps";
import { Formik } from "formik";
import * as Yup from "yup";
import { badgesDuration, badgesReqValues } from "../../services/staticData";

const AddEditBadgeModal = ({
  showModal,
  setShowModal,
  selectedBadge,
  setSelectedBadge,
  fetchBades,
  pagination,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imageURL, setImageURL] = useState(null);
  const [imageChanged, setImageChanged] = useState(false);
  const [reqValues, setReqValue] = useState([]);

  const badgeValidations = Yup.object().shape({
    name: Yup.string()
      .trim()
      .min(2, "Achievement name must be at least 2 characters")
      .max(20, "Achievement name cannot exceed 20 characters")
      .required("Achievement name is required"),
    description: Yup.string().trim().required("Description is required."),
    rank_value: Yup.number()
      .typeError("Rank Value must be a number")
      .min(0, "Rank Value must be at least 0"),
    icon: Yup.mixed().required("Icon is required"),
    type: Yup.string().trim().required("Please select a badge type"),
    adsCount: Yup.string().trim().required("Please select ads requirement"),
  });

  const handleCloseAddEditModal = () => {
    setSelectedBadge({});
    setShowModal(false);
    setImageURL(null);
    setReqValue([]);
    setImageChanged(false);
  };

  const uploadIcon = async (id, icon) => {
    if (imageChanged) {
      const formData = new FormData();
      formData.append("image", icon);
      const res = await uploadBadgeIcon(id, formData);
      if (res?.data.success) {
        toast.success("Badge Icon has been uploaded", { duration: 2000 });
      } else {
        handleErrors(res, navigate);
      }
    }
  };

  const handleSubmitForm = async (data) => {
    setLoading(true);
    if (selectedBadge?.id) {
      // // // Edit Badge
      const { icon, ...values } = data;
      const res = await updateBadge({ id: selectedBadge.id, ...values });
      if (res?.data?.success) {
        await uploadIcon(selectedBadge.id, icon);
        await fetchBades(pagination);
        toast.success("Badge has been updated.", { duration: 2000 });
        handleCloseAddEditModal();
      } else {
        handleErrors(res, navigate, "Update Badge:");
      }
    } else {
      // // // Add Badge
      const { icon, ...values } = data;
      const res = await addNewBadge({ ...values });
      if (res?.data?.success) {
        const id = res?.data?.data?.id;
        if (id) {
          await uploadIcon(id, icon);
        }
        await fetchBades(pagination);
        toast.success("A new Badge has been added.", { duration: 2000 });
        if (id) handleCloseAddEditModal();
      } else {
        handleErrors(res, navigate, "Add Badge:");
      }
    }
    setLoading(false);
  };

  const makeIconURL = (file) => {
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setImageURL(previewUrl);
      setImageChanged(true);
    }
  };

  useEffect(() => {
    if (showModal && selectedBadge) {
      if (selectedBadge?.badge_type) {
        setReqValue(badgesReqValues[selectedBadge?.badge_type.toLowerCase()]);
      }
    }
  }, [showModal]);
  return (
    <Modal
      show={showModal}
      onHide={handleCloseAddEditModal}
      centered
      className="text-light"
      data-testid="modal"
    >
      <Modal.Header closeButton className="bg-dark text-light border-0">
        <Modal.Title>
          {selectedBadge?.id ? "Update Badge" : "Add New Badge"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-light">
        <Formik
          initialValues={{
            name: selectedBadge?.name || "",
            description: selectedBadge?.description || "",
            rank_value: selectedBadge?.rank_value || 1,
            type: selectedBadge?.requirements
              ? selectedBadge?.requirements?.split(">=")[0]
              : "",
            adsCount: selectedBadge?.requirements
              ? selectedBadge?.requirements?.split(">=")[1]
              : "",
            // requirements: selectedBadge?.requirements || "",
            icon: selectedBadge?.icon || null,
          }}
          validationSchema={badgeValidations}
          enableReinitialize
          onSubmit={async (values) => {
            try {
              const data = {
                ...values,
                requirements: `${values.type}>=${values.adsCount}`,
                badge_type: values.type.toUpperCase(),
              };
              setLoading(true);
              await handleSubmitForm(data);
            } catch (error) {
              console.log("Error upon Add Update Achievement", error);
            } finally {
              setLoading(false);
            }
          }}
        >
          {({
            handleChange,
            setFieldValue,
            handleSubmit,
            values,
            errors,
            touched,
          }) => (
            <div>
              <Form.Group className="mb-3">
                <Form.Label>Badge Name</Form.Label>
                <Form.Control
                  data-testid="form-control-name"
                  type="text"
                  placeholder="Enter Badge name"
                  required
                  name="name"
                  value={values.name}
                  onChange={handleChange("name")}
                  className="form-control bg-dark text-light border-light input-placeholder-light"
                />
                {errors?.name && touched?.name ? (
                  <span className="text-danger">{errors?.name}</span>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  data-testid="form-control-description"
                  type="text"
                  placeholder="Enter Description"
                  required
                  name="description"
                  value={values.description}
                  onChange={handleChange("description")}
                  className="form-control bg-dark text-light border-light input-placeholder-light"
                />
                {errors?.description && touched?.description ? (
                  <span className="text-danger">{errors?.description}</span>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Badge Type(Requirements)</Form.Label>
                <Form.Control
                  as="select"
                  id="type"
                  value={values.type}
                  data-testid="req-type"
                  onChange={(e) => {
                    setFieldValue("type", e.target.value);
                    e?.target?.value
                      ? setReqValue(badgesReqValues[e.target.value])
                      : setReqValue([]);
                    setFieldValue("adsCount", "");
                  }}
                  className="bg-dark text-light border-light"
                >
                  <option value="">Select Badge Type</option>
                  {badgesDuration?.map((m) => (
                    <option value={m}>{m}</option>
                  ))}
                </Form.Control>
                {errors.type && touched.type ? (
                  <span className="text-danger">{errors.type}</span>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Number of Ads(Requirements)</Form.Label>
                <Form.Control
                  as="select"
                  id="req-val"
                  value={values.adsCount}
                  data-testid="req-count"
                  onChange={(e) => {
                    setFieldValue("adsCount", e.target.value);
                  }}
                  className="bg-dark text-light border-light"
                >
                  <option value="">
                    {values.type
                      ? "Select Ads Count"
                      : "Select badge type first"}
                  </option>
                  {reqValues?.map((m) => (
                    <option value={m}>{m}</option>
                  ))}
                </Form.Control>
                {errors.adsCount && touched.adsCount ? (
                  <span className="text-danger">{errors.adsCount}</span>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Rank Value</Form.Label>
                <Form.Control
                  data-testid="form-control-rank_value"
                  type="number"
                  placeholder="Enter Rank Value"
                  required
                  name="rank_value"
                  value={values.rank_value}
                  onChange={handleChange("rank_value")}
                  className="form-control bg-dark text-light border-light input-placeholder-light"
                />
                {errors?.rank_value && touched?.rank_value ? (
                  <span className="text-danger">{errors?.rank_value}</span>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>badge Icon</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/png, image/jpeg, image/webp"
                  className="bg-dark text-light border-light"
                  data-testid="form-control-icon"
                  onChange={(event) => {
                    if (event?.currentTarget?.files?.length) {
                      if (!validateImage(event.currentTarget.files[0])) {
                        event.currentTarget.value = "";
                        return;
                      }
                      setFieldValue("icon", event.currentTarget.files[0]);
                      makeIconURL(event.currentTarget.files[0]);
                    } else {
                      setFieldValue("icon", selectedBadge.icon);
                      setImageURL(null);
                    }
                  }}
                />
                {<span>Maximum file size: 10MB</span>}
                <br></br>
                {imageURL ||
                  (selectedBadge?.icon && (
                    <img
                      crossorigin="anonymous"
                      src={
                        imageURL
                          ? imageURL
                          : selectedBadge?.icon
                            ? `${baseURL}/uploads/file/${selectedBadge.icon}`
                            : null
                      }
                      style={{
                        maxWidth: "20%",
                        marginTop: "10px",
                        borderRadius: "5px",
                      }}
                    />
                  ))}
                {errors?.icon && touched?.icon ? (
                  <span className="text-danger">{errors?.icon}</span>
                ) : null}
              </Form.Group>

              <div className="constainer-fluid">
                <div className="d-flex justify-content-end">
                  {loading && (
                    <div className="mt-4 mx-2">
                      <LoadingSpinner size={"lg"} />
                    </div>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => {
                      handleCloseAddEditModal();
                    }}
                    className="mt-3 mx-2"
                    data-testid="modal-close"
                  >
                    Close
                  </Button>
                  <Button
                    variant="success"
                    className="mt-3"
                    data-testid="Add-Badge-Button"
                    onClick={() => {
                      handleSubmit();
                    }}
                  >
                    {selectedBadge?.id ? "Edit Badge" : "Add Badge"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};
export default AddEditBadgeModal;
